import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ComparisonTableSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Alle oplossingen', icon: 'Grid' },
    { id: 'solar', name: 'Zonnepanelen', icon: 'Sun' },
    { id: 'heating', name: 'Verwarming', icon: 'Thermometer' },
    { id: 'insulation', name: 'Isolatie', icon: 'Home' }
  ];

  const comparisons = [
    {
      category: 'solar',
      solution: 'Zonnepanelen (10 panelen)',
      investment: '€5.000',
      subsidy: '€0 (BTW terug)',
      yearlyReturn: '€800',
      payback: '7-8 jaar',
      lifespan: '25-30 jaar',
      roi: '300-400%'
    },
    {
      category: 'solar',
      solution: 'Zonnepanelen (20 panelen)',
      investment: '€9.000',
      subsidy: '€0 (BTW terug)',
      yearlyReturn: '€1.500',
      payback: '7-9 jaar',
      lifespan: '25-30 jaar',
      roi: '400-500%'
    },
    {
      category: 'heating',
      solution: 'Hybride warmtepomp',
      investment: '€8.000',
      subsidy: '€2.000 (ISDE)',
      yearlyReturn: '€600',
      payback: '10-12 jaar',
      lifespan: '15-20 jaar',
      roi: '150-200%'
    },
    {
      category: 'heating',
      solution: 'All-electric warmtepomp',
      investment: '€15.000',
      subsidy: '€3.000 (ISDE)',
      yearlyReturn: '€1.200',
      payback: '12-15 jaar',
      lifespan: '15-20 jaar',
      roi: '150-200%'
    },
    {
      category: 'insulation',
      solution: 'Spouwmuurisolatie',
      investment: '€2.500',
      subsidy: '€750 (ISDE)',
      yearlyReturn: '€300',
      payback: '7-9 jaar',
      lifespan: '40+ jaar',
      roi: '400-500%'
    },
    {
      category: 'insulation',
      solution: 'Dakisolatie',
      investment: '€3.500',
      subsidy: '€1.000 (ISDE)',
      yearlyReturn: '€400',
      payback: '7-10 jaar',
      lifespan: '40+ jaar',
      roi: '400-500%'
    },
    {
      category: 'insulation',
      solution: 'Vloerisolatie',
      investment: '€4.000',
      subsidy: '€1.200 (ISDE)',
      yearlyReturn: '€350',
      payback: '9-12 jaar',
      lifespan: '40+ jaar',
      roi: '300-400%'
    }
  ];

  const filteredComparisons = activeCategory === 'all' 
    ? comparisons 
    : comparisons?.filter(c => c?.category === activeCategory);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Gedetailleerde kostenvergelijking
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Vergelijk investeringen, subsidies en terugverdientijden van verschillende oplossingen
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-8"
        >
          {categories?.map((category) => (
            <motion.button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                activeCategory === category?.id
                  ? 'bg-primary text-white shadow-elevation-2'
                  : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
              }`}
            >
              <Icon name={category?.icon} size={20} />
              <span>{category?.name}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="overflow-x-auto"
        >
          <div className="min-w-full bg-white rounded-2xl shadow-elevation-2 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-primary to-blue-600 text-white">
              <div className="grid grid-cols-7 gap-4 p-4 text-sm font-semibold">
                <div className="col-span-2">Oplossing</div>
                <div>Investering</div>
                <div>Subsidie</div>
                <div>Jaar besparing</div>
                <div>Terugverdientijd</div>
                <div>ROI</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
              {filteredComparisons?.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}
                  className="grid grid-cols-7 gap-4 p-4 text-sm items-center"
                >
                  <div className="col-span-2 font-semibold text-foreground">{item?.solution}</div>
                  <div className="text-muted-foreground">{item?.investment}</div>
                  <div className="text-success font-semibold">{item?.subsidy}</div>
                  <div className="text-muted-foreground">{item?.yearlyReturn}</div>
                  <div className="text-primary font-semibold">{item?.payback}</div>
                  <div className="text-success font-semibold">{item?.roi}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4">
            <Icon name="Info" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">ROI</p>
              <p className="text-xs text-muted-foreground">Return on Investment over levensduur</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4">
            <Icon name="Gift" size={20} className="text-success" />
            <div>
              <p className="text-sm font-semibold text-foreground">ISDE</p>
              <p className="text-xs text-muted-foreground">Investeringssubsidie Duurzame Energie</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-4">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">Prijsstijging</p>
              <p className="text-xs text-muted-foreground">Berekeningen gebaseerd op huidige prijzen</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonTableSection;